import { Box, Typography, TextField } from "@mui/material";
import { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { colorsFoundation } from "../../../theme/index.js";

const ACCENT = "#4A78BC";

const compose = (strasse, plz, stadt) =>
  [strasse, [plz, stadt].filter(Boolean).join(" ")].filter(Boolean).join(", ");

/**
 * "Wo ist der Schaden entstanden?" — lets the customer choose between their own
 * address and a different location, entering the address for the latter. Matches
 * the Schmidt click dummy. Resolves the chosen location into `schaden_ort`.
 */
export default function ClaimLocation({
  model = {},
  values = {},
  onChange,
  disabled = false,
}) {
  const ownAddress = model.bezeichnung || model.objekt_adresse || "meiner Anschrift";
  const abweichend = Boolean(values.schaden_ort_abweichend);
  const set = (name, value) => onChange?.({ target: { name, value } });

  // Default to "An meiner Anschrift" and seed schaden_ort with it once.
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current || disabled) return;
    seeded.current = true;
    if (!abweichend && !values.schaden_ort && ownAddress) {
      set("schaden_ort", ownAddress);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chooseOwn = () => {
    if (disabled) return;
    set("schaden_ort_abweichend", false);
    set("schaden_ort", ownAddress);
  };
  const chooseOther = () => {
    if (disabled) return;
    set("schaden_ort_abweichend", true);
    set(
      "schaden_ort",
      compose(
        values.schaden_ort_strasse,
        values.schaden_ort_plz,
        values.schaden_ort_stadt
      )
    );
  };
  const setPart = (part, val) => {
    const next = {
      schaden_ort_strasse: values.schaden_ort_strasse,
      schaden_ort_plz: values.schaden_ort_plz,
      schaden_ort_stadt: values.schaden_ort_stadt,
      [part]: val,
    };
    set(part, val);
    set(
      "schaden_ort",
      compose(
        next.schaden_ort_strasse,
        next.schaden_ort_plz,
        next.schaden_ort_stadt
      )
    );
  };

  const optionRow = (selected, onClick, title, sub) => (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={onClick}
      sx={{
        width: "100%",
        textAlign: "left",
        cursor: disabled ? "default" : "pointer",
        fontFamily: "inherit",
        display: "flex",
        alignItems: "flex-start",
        gap: "11px",
        p: "13px 15px",
        borderRadius: "11px",
        border: `1.5px solid ${selected ? ACCENT : "#dde3ec"}`,
        backgroundColor: selected ? "#f4f8fd" : "#fff",
      }}
    >
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: "50%",
          flex: "none",
          mt: "1px",
          boxSizing: "border-box",
          border: selected ? `5px solid ${ACCENT}` : "2px solid #cbd5e1",
        }}
      />
      <Box>
        <Typography
          sx={{
            fontSize: "13.5px",
            fontWeight: 600,
            color: colorsFoundation.gray[900],
          }}
        >
          {title}
        </Typography>
        <Typography sx={{ fontSize: "12.5px", color: "#6b7280", mt: "1px" }}>
          {sub}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "20px" }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {optionRow(!abweichend, chooseOwn, "An meiner Anschrift", ownAddress)}
        {optionRow(
          abweichend,
          chooseOther,
          "An einem anderen Ort",
          "abweichende Adresse angeben"
        )}
      </Box>

      {abweichend && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr 1.4fr",
            gap: "14px",
          }}
        >
          <TextField
            label="Straße & Nr."
            size="small"
            fullWidth
            disabled={disabled}
            value={values.schaden_ort_strasse || ""}
            onChange={(e) => setPart("schaden_ort_strasse", e.target.value)}
          />
          <TextField
            label="PLZ"
            size="small"
            fullWidth
            disabled={disabled}
            value={values.schaden_ort_plz || ""}
            onChange={(e) => setPart("schaden_ort_plz", e.target.value)}
          />
          <TextField
            label="Ort"
            size="small"
            fullWidth
            disabled={disabled}
            value={values.schaden_ort_stadt || ""}
            onChange={(e) => setPart("schaden_ort_stadt", e.target.value)}
          />
        </Box>
      )}
    </Box>
  );
}

ClaimLocation.propTypes = {
  model: PropTypes.object,
  values: PropTypes.object,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
};
